import { Box } from "@mui/material";
import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Vector2d } from "konva/lib/types";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import useImage from "use-image";

import { StrokeWidth } from "components/detector/NotesModal/NotesContent/NotesSection/CustomEditor/ImageUploader/ImageAnnotatorModal/ImageAnnotator/AnnotatorTools/StrokeSettings/StrokeSettings";
import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { useAppDispatch } from "hooks/store";
import { setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ValidBoxLabelsProps } from "types/boundingBoxesContextTypes";
import { CanvasAnnotation, CanvasAnnotationUpdate, CanvasAnnotationUpdater } from "types/imageAnnotations";
import { generateId } from "utils/generateId";
import { getFileFromDataUrl } from "utils/imgtools";

import { CanvasRenderer, CanvasRendererProps } from "./components/CanvasRenderer/CanvasRenderer";
import CanvasRendererWrapper from "./components/CanvasRendererWrapper";
import { ContextMenu } from "./components/ContextMenu";
import { ShapeType } from "./components/ShapeRenderer";
import { BOUNDING_BOXES_COLORS } from "./constants";
import { useCanvasImage } from "./hooks/useCanvasImage";
import { getPointerPosition, useDrawBoundingBox } from "./hooks/useDrawBoundingBox";
import { useResize } from "./hooks/useResize";
import { useZoomOptimizedCanvas } from "./hooks/useZoomOptimizedCanvas";
import { Point, Size } from "./types";
import {
  calculateGeometry,
  clampPointToCanvas,
  parseGeometryToPoints,
  rotatedDimension,
  rotateDimensions,
} from "./utils";

export const DEFAULT_MIN_WIDTH = 10;
export const DEFAULT_MIN_HEIGHT = 10;

export const ANNOTATION_TEXT_MIN_WIDTH = 35;
export const ANNOTATION_TEXT_MIN_HEIGHT = 35;

const SCALE_RATE = 1.2;
const PANNING_PADDING_PX = 10;

const DEFAULT_ANNOTATION_STROKE_WIDTH = 2;
const DEFAULT_ANNOTATION_FONT_SIZE = 24;

const SHOW_CONTEXT_MENU_DELAY_MS = 100;

function isLeftClick(event: KonvaEventObject<MouseEvent>): boolean {
  return (event.evt.ctrlKey && event.evt.button === 0) || event.evt.button >= 1;
}

export type AnnotationSettings = {
  color?: string;
  width?: StrokeWidth;
};

// Props for the Canvas component
// All props are used in the component, even if ESLint doesn't recognize it
/* eslint-disable react/no-unused-prop-types */
interface CanvasProps {
  /** Annotations to display on the canvas */
  annotations: CanvasAnnotation[];
  /** The ID of the image query, used for generating the download filename */
  iqId?: string;
  /** Current box being drawn */
  currentBox?: ValidBoxLabelsProps;
  /** Whether annotation is enabled */
  enableAnnotation?: boolean;
  /** URL of the image to display */
  imageUrl?: string;
  /** Image data and loading state */
  imageData?: [undefined | HTMLImageElement, "loaded" | "loading" | "failed"];
  /** Currently selected annotation */
  selectedAnnotation?: CanvasAnnotation;
  /** Function to update annotations */
  setAnnotations?: React.Dispatch<React.SetStateAction<CanvasAnnotation[]>>;
  /** Function to update selected annotation */
  setSelectedAnnotation?: React.Dispatch<React.SetStateAction<CanvasAnnotation | undefined>>;
  /** Custom styles for the canvas */
  style?: React.CSSProperties;
  /** Whether pan and zoom is enabled */
  enablePanZoom?: boolean;
  /** Whether the canvas can be interacted with */
  canInteract?: boolean;
  /** Whether to hide labels */
  hideLabel?: boolean;
  /** Maximum number of bounding boxes */
  maxBoundingBoxes?: number;
  /** Size of the box container */
  boxContainerSize: Size;
  /** Function to update box container size */
  setBoxContainerSize: (boxContainerSize: Size) => void;
  /** Position of the canvas */
  position: Point;
  /** Function to update position */
  setPosition: (position: Point) => void;
  /** Whether to show bounding boxes */
  showBoundingBoxes?: boolean;
  /** Annotation shape */
  annotationShape?: ShapeType;
  /** Hide annotation middle cross */
  hideAnnotationMiddleCross?: boolean;
  /** Hide annotation's delete action */
  hideAnnotationDelete?: boolean;
  /** Callback for when user clicks in annotation */
  onAnnotationClick?: (annotationId: string) => void;

  annotationSettings?: AnnotationSettings;
  allowSmallAnnotations?: boolean;
  /** Whether to dispatch unsaved annotations flag when annotations change */
  notifyUnsavedAnnotations?: boolean;
}

export const isSmallBbox = (startPoint: Point, endPoint: Point, annotationShape: ShapeType) => {
  // Check if the rectangle meets the minimum size requirements
  const width = Math.abs(startPoint.x - endPoint.x);
  const height = Math.abs(startPoint.y - endPoint.y);

  if (annotationShape === "text") {
    return width < ANNOTATION_TEXT_MIN_WIDTH || height < ANNOTATION_TEXT_MIN_HEIGHT;
  }

  return width < DEFAULT_MIN_WIDTH || height < DEFAULT_MIN_HEIGHT;
};

export interface CanvasRef {
  downloadImage: (filename: string) => Promise<void>;
  toDataUrl: () => Promise<string | undefined>;
  generateImage: () => Promise<File | null>;
  blurAnnotations: () => void;
}

// eslint-disable-next-line prefer-arrow-callback
export const Canvas = React.memo(
  forwardRef<CanvasRef, CanvasProps>((props, ref) => {
    const {
      annotations,
      currentBox,
      enableAnnotation = false,
      imageUrl = "",
      iqId = "query",
      selectedAnnotation,
      setAnnotations,
      setSelectedAnnotation,
      style,
      enablePanZoom = false,
      canInteract = true,
      hideLabel = false,
      maxBoundingBoxes = Infinity,
      imageData,
      position,
      setPosition,
      boxContainerSize,
      setBoxContainerSize,
      showBoundingBoxes = true,
      annotationShape = "rectangle",
      hideAnnotationMiddleCross = false,
      hideAnnotationDelete = false,
      onAnnotationClick,
      annotationSettings,
      allowSmallAnnotations,
      notifyUnsavedAnnotations = true,
    } = props;

    // Add state for context menu
    const [contextMenu, setContextMenu] = useState<{
      visible: boolean;
      x: number;
      y: number;
    }>({
      visible: false,
      x: 0,
      y: 0,
    });

    const mousePositionRef = useRef<Point | null>({
      x: 0,
      y: 0,
    });
    const [mouseMoved, setMouseMoved] = useState(false);

    const {
      hoveredNode,
      setHoveredNode,
      getBoundingBoxCursor,
      isSettingsExpanded,
      setShowAnnotations,
      isDragging,
      validBoxLabels,
      setIsDragging,
      canDraw,
    } = useBoundingBoxesContext();

    const { zoom = 0, imageSettings, isEditing, setZoom } = useImageSettingsContext();
    const isSetPositionRef = useRef(false);

    // Ref to store the initial mouse position when starting to pan
    const downPoint = useRef<Point | null>(null);
    const startMouseDownPoint = useRef<Point | null>(null);

    const canPan = zoom > 1 && enablePanZoom;
    const canZoom = enablePanZoom;

    const rotationValue = imageSettings.rotation ?? 0;
    const isRotated = rotationValue === 90 || rotationValue === 270;

    const [currentCursor, setCurrentCursor] = useState<React.CSSProperties["cursor"]>("default");
    const dispatch = useAppDispatch();

    const label = currentBox?.label;
    const availabeColors = BOUNDING_BOXES_COLORS.filter(
      (color) => !annotations.some((annotation) => annotation.strokeColor === color)
    );
    const colorsToUse = availabeColors.length === BOUNDING_BOXES_COLORS.length ? BOUNDING_BOXES_COLORS : availabeColors;
    const color = validBoxLabels.length > 1 && currentBox?.color ? currentBox.color : colorsToUse[0];
    const boxContainerRef = useRef<HTMLDivElement | null>(null);

    const canvasRef = useRef<Konva.Stage | null>(null);
    const canvasExportRef = useRef<Konva.Stage | null>(null);

    const ownImageData = useImage(imageUrl);
    const [originalImage, imageStatus] = imageData ?? ownImageData;
    const originalCanvasImage = useCanvasImage(originalImage, imageStatus === "loaded");
    const image = useZoomOptimizedCanvas(originalCanvasImage, canZoom);

    const canvasImageRef = useRef<Konva.Image | null>(null);
    const { canvasDimensions: normalImageDimensions } = useResize(
      image,
      boxContainerSize,
      isRotated,
      isSettingsExpanded
    );

    const zoomedDimensions = useMemo(
      () => ({
        width: normalImageDimensions.width * zoom,
        height: normalImageDimensions.height * zoom,
      }),
      [normalImageDimensions.height, normalImageDimensions.width, zoom]
    );

    const rotatedCanvasDimensions = useMemo(
      () => ({
        width: rotatedDimension(normalImageDimensions, { property: "width", rotateDimensions: isRotated }),
        height: rotatedDimension(normalImageDimensions, { property: "height", rotateDimensions: isRotated }),
      }),
      [normalImageDimensions, isRotated]
    );

    // Optimize annotation processing by memoizing rotatedDimensions calculation outside the map function
    const rotatedDimensions = useMemo(
      () => rotateDimensions(normalImageDimensions, isRotated),
      [normalImageDimensions, isRotated]
    );

    const offset = useMemo(
      () => ({
        x: rotatedDimension(normalImageDimensions, { property: "width", rotateDimensions: isRotated }) / 2,
        y: rotatedDimension(normalImageDimensions, { property: "height", rotateDimensions: isRotated }) / 2,
      }),
      [normalImageDimensions, isRotated]
    );

    const canvasWidth = boxContainerSize.width;
    const canvasHeight = boxContainerSize.height;

    const imageWidth = rotatedDimension(normalImageDimensions, { property: "width", rotateDimensions: isRotated });
    const imageHeight = rotatedDimension(normalImageDimensions, { property: "height", rotateDimensions: isRotated });

    const centerPoint: Point = useMemo(
      () => ({
        x: canvasWidth / 2,
        y: canvasHeight / 2,
      }),
      [canvasWidth, canvasHeight]
    );

    const zoomImageWidth = !isRotated ? imageWidth * zoom : imageHeight * zoom;
    const zoomImageHeight = !isRotated ? imageHeight * zoom : imageWidth * zoom;

    // This effect is required for the image to be able to apply brightness and opacity changes
    useEffect(() => {
      const currentCanvas = canvasImageRef.current;
      const width = currentCanvas?.width() || NaN;
      const height = currentCanvas?.height() || NaN;
      if (currentCanvas && !Number.isNaN(width) && !Number.isNaN(height)) {
        canvasImageRef.current?.cache();
      }
    }, [canvasImageRef, normalImageDimensions]);

    useEffect(() => {
      if (!canvasRef.current) return;

      canvasRef.current.offset(offset);
      canvasRef.current.position(position);
    }, [offset, position]);

    useEffect(() => {
      if (!isSetPositionRef.current) {
        setPosition(centerPoint);
      }
    }, [centerPoint, setPosition, isSetPositionRef]);

    const canDrawMoreBoundingBoxes = annotations.length <= maxBoundingBoxes;
    const getCursor = useCallback((): React.CSSProperties["cursor"] => {
      if (isDragging && canPan) return "grabbing";
      if (isEditing && enableAnnotation)
        return getBoundingBoxCursor?.(isRotated, imageSettings.flipX, imageSettings.flipY);
      return "default";
    }, [
      isDragging,
      canPan,
      isEditing,
      enableAnnotation,
      getBoundingBoxCursor,
      isRotated,
      imageSettings.flipX,
      imageSettings.flipY,
    ]);

    // Reset drawing states to allow new annotations
    const resetDrawingStates = useCallback(() => {
      setHoveredNode("none");
      setCurrentCursor(canDraw ? "crosshair" : undefined);
    }, [setHoveredNode, canDraw]);

    // delete annotation
    const deleteAnnotation = useCallback(
      (id: string) => {
        if (setAnnotations && setSelectedAnnotation && canInteract) {
          const filteredAnnotations = annotations?.filter((annotation) => annotation.id !== id) || [];
          if (notifyUnsavedAnnotations) {
            dispatch(setUnsavedAnnotations(true));
          }
          setAnnotations(filteredAnnotations);
          setSelectedAnnotation(undefined);
        }
      },
      [canInteract, annotations, dispatch, setAnnotations, setSelectedAnnotation, notifyUnsavedAnnotations]
    );

    const [relativePointerPosition, setRelativePointerPosition] = useState<{ x: number; y: number } | null>(null);
    const [isMouseInsideCanvas, setIsMouseInsideCanvas] = useState(false);
    const [isMouseInsideImage, setIsMouseInsideImage] = useState(false);
    // composed variable from image and canvas hover state since they are in
    // different layers so when the mouse switches between them there will be a
    // small time that the mouse will be considered outside
    const isMouseInsideCanvasOrImage = isMouseInsideCanvas || isMouseInsideImage;

    const scaleOnScroll = useCallback(
      (event: KonvaEventObject<WheelEvent>) => {
        if (!canvasRef.current || !isMouseInsideImage) return;

        const stage = canvasRef.current;
        const oldScale = stage.scaleX();
        // Get absolute scale value (ignoring sign which indicates flip state)
        const absOldScale = Math.abs(oldScale);
        const pointerPos = stage.getPointerPosition() as Vector2d;

        // We always use real coordinates for zoom, regardless of the flip
        // to maintain consistent behavior
        const pointTo = {
          x: (pointerPos.x - position.x) / absOldScale,
          y: (pointerPos.y - position.y) / absOldScale,
        };

        // Determine new scale (absolute value)
        let newAbsScale;
        if (event.evt.deltaY < 0) {
          // Zoom in
          newAbsScale = absOldScale * SCALE_RATE;
        } else {
          // Zoom out
          newAbsScale = absOldScale / SCALE_RATE;
        }
        // Clamp between 1 and 8
        newAbsScale = Math.min(Math.max(newAbsScale, 1), 8);

        // Set new zoom level
        setZoom(newAbsScale);

        if (newAbsScale > 1) {
          // Calculate the new position that will keep the pointer at the same relative position
          // We maintain consistent behavior regardless of the flip
          const newX = pointerPos.x - pointTo.x * newAbsScale;
          const newY = pointerPos.y - pointTo.y * newAbsScale;

          setPosition({ x: newX, y: newY });
          isSetPositionRef.current = true;
        } else {
          // Reset to center if zoom level is 1
          setPosition(centerPoint);
          isSetPositionRef.current = false;
        }

        stage.batchDraw();
      },
      [canvasRef, isMouseInsideImage, setZoom, setPosition, centerPoint, position]
    );

    // Utility function to apply limits to a coordinate
    const applyPositionLimits = useCallback(
      (value: number, isX: boolean) => {
        const maxPan = Math.max(
          0,
          ((isX ? zoomImageWidth : zoomImageHeight) - (isX ? canvasWidth : canvasHeight)) / 2 + PANNING_PADDING_PX
        );

        const centerValue = centerPoint[isX ? "x" : "y"];
        const currentValue = position[isX ? "x" : "y"];

        // Check if we are outside the limits consistently
        // without considering the flip state
        const outsidePositiveLimit = currentValue > centerValue + maxPan;
        const outsideNegativeLimit = currentValue < centerValue - maxPan;

        if (outsidePositiveLimit) {
          // If we are outside the positive limit, only allow movement inward
          return value <= currentValue ? value : currentValue;
        }

        if (outsideNegativeLimit) {
          // If we are outside the negative limit, only allow movement inward
          return value >= currentValue ? value : currentValue;
        }

        // Normal case: if we are within limits, apply restrictions
        // to avoid going outside the limits
        return Math.max(centerValue - maxPan, Math.min(centerValue + maxPan, value));
      },
      [centerPoint, canvasWidth, canvasHeight, zoomImageWidth, zoomImageHeight, position]
    );

    const moveCanvas = useCallback(
      (event: KonvaEventObject<WheelEvent>, axis?: "x" | "y") => {
        if (!axis) {
          // Calculate new position based on wheel event
          // We maintain consistent behavior without considering the flip
          const newX = position.x - event.evt.deltaX;
          const newY = position.y - event.evt.deltaY;

          // Apply limits to both axes
          setPosition({
            x: applyPositionLimits(newX, true),
            y: applyPositionLimits(newY, false),
          });
        } else {
          const newPosition = { ...position };

          if (axis === "x") {
            // Consistent use without considering the flip
            newPosition.x -= event.evt.deltaX || event.evt.deltaY;
            newPosition.x = applyPositionLimits(newPosition.x, true);
          }
          if (axis === "y") {
            // Consistent use without considering the flip
            newPosition.y -= event.evt.deltaY || event.evt.deltaX;
            newPosition.y = applyPositionLimits(newPosition.y, false);
          }

          setPosition(newPosition);
        }

        isSetPositionRef.current = true;
      },
      [position, setPosition, applyPositionLimits]
    );

    // Below are defined the handler functions. Ideally these methods shouldn't handle any business/rendering logic or
    // they could become too big and unmaintainable. Try to keep the logic inside methods in this component or in custom
    // hooks like the `getCursor` defined in this method or `startDrawingBoundingBox` defined in the hook
    // `useDrawBoundingBox`

    const annotationStrokeWidth = annotationSettings?.width ?? DEFAULT_ANNOTATION_STROKE_WIDTH;
    const annotationStrokeColor = annotationSettings?.color ?? color;

    // Handler for mouse up event
    const saveDrawingAnnotation = useCallback(
      (newDrawingStartPoint: Point | null, newDrawingEndPoint: Point | null) => {
        if (newDrawingStartPoint && newDrawingEndPoint) {
          // Adjust end point to ensure it remains inside the canvas
          const rotatedDimensions = rotateDimensions(normalImageDimensions, isRotated);
          const end = clampPointToCanvas(newDrawingEndPoint, rotatedDimensions);

          // Check if the rectangle meets the minimum size requirements
          if (isSmallBbox(newDrawingStartPoint, end, annotationShape) && !allowSmallAnnotations) {
            resetDrawingStates();
            return;
          }
          const geometry = calculateGeometry(newDrawingStartPoint, end, rotatedDimensions);

          const normalStart = {
            x: geometry.left,
            y: geometry.top,
          };
          const normalEnd = {
            x: geometry.right,
            y: geometry.bottom,
          };

          const roi: CanvasAnnotation = {
            id: generateId(),
            label: label ?? "",
            strokeColor: annotationStrokeColor,
            strokeWidth: annotationStrokeWidth,
            shape: annotationShape,
            start: normalStart,
            end: normalEnd,
            text: annotationShape === "text" ? "" : undefined,
            fontSize: annotationShape === "text" ? DEFAULT_ANNOTATION_FONT_SIZE : undefined,
          };

          const width = roi.end.x - roi.start.x;
          const height = roi.end.y - roi.start.y;

          const redimensionedROI: CanvasAnnotation = {
            ...roi,
            end:
              annotationShape === "text"
                ? {
                    ...roi.end,
                    // if text annotation is drawn with small box set it to minimum size
                    x: roi.start.x + Math.max(width, ANNOTATION_TEXT_MIN_WIDTH / rotatedDimensions.width),
                    y: roi.start.y + Math.max(height, ANNOTATION_TEXT_MIN_HEIGHT / rotatedDimensions.height),
                  }
                : roi.end,
          };

          // text annotations by default are drawed in selected mode
          if (annotationShape === "text") {
            setSelectedAnnotation?.(redimensionedROI);
            setDoubleClickedAnnotation?.(redimensionedROI);
          }

          if (setAnnotations) {
            if (notifyUnsavedAnnotations) {
              dispatch(setUnsavedAnnotations(true));
            }
            setAnnotations([...annotations, redimensionedROI]);

            // always show bounding boxes after creating a new one
            setShowAnnotations(true);
            resetDrawingStates();
          }
        }
      },
      [
        setAnnotations,
        dispatch,
        normalImageDimensions,
        isRotated,
        annotations,
        label,
        setShowAnnotations,
        annotationShape,
        annotationStrokeWidth,
        annotationStrokeColor,
        allowSmallAnnotations,
        setSelectedAnnotation,
        resetDrawingStates,
        notifyUnsavedAnnotations
      ]
    );

    const { startDrawingBoundingBox, drawingKeyEvents, drawingStartPoint, drawingEndPoint } = useDrawBoundingBox({
      canvasDimensions: normalImageDimensions,
      isRotated,
      canvasRef,
      onEndDrawingBoundingBox: saveDrawingAnnotation,
    });

    const [doubleClickedAnnotation, setDoubleClickedAnnotation] = useState<CanvasAnnotation | null>(null);

    // This effect allows to delete a bounding box with the delete or backspace keys
    useEffect(() => {
      const keydownListener = (event: KeyboardEvent) => {
        if (enableAnnotation) drawingKeyEvents(event);

        // when a text annotation is double clicked (editing text) the backspace
        // should delete text and not the annotation
        const shouldAvoidDelete =
          selectedAnnotation?.id === doubleClickedAnnotation?.id && selectedAnnotation?.shape === "text";

        if (selectedAnnotation?.id && !shouldAvoidDelete && (event.key === "Delete" || event.key === "Backspace")) {
          deleteAnnotation(selectedAnnotation.id);
          resetDrawingStates();
        }
      };
      document.addEventListener("keydown", keydownListener);

      return () => {
        document.removeEventListener("keydown", keydownListener);
      };
    }, [
      enableAnnotation,
      selectedAnnotation,
      deleteAnnotation,
      drawingKeyEvents,
      resetDrawingStates,
      doubleClickedAnnotation,
    ]);

    const handleBlurAnnotation = useCallback(
      (annotationId: string) => {
        const annotation = annotations.find((annotation) => annotation.id === annotationId);

        // if text annotation ends without content delete it to avoid invisible clickable elements
        if (annotation && annotation.shape === "text" && !annotation.text) {
          deleteAnnotation(annotation.id);
        }
      },
      [deleteAnnotation, annotations]
    );

    const handleBlurAnnotations = useCallback(() => {
      if (selectedAnnotation) {
        handleBlurAnnotation(selectedAnnotation.id);
      }

      setSelectedAnnotation?.(undefined);
      setDoubleClickedAnnotation(null);
    }, [setSelectedAnnotation, handleBlurAnnotation, selectedAnnotation]);

    const handleMouseDown = useCallback(
      (event: KonvaEventObject<MouseEvent>) => {
        event.evt.preventDefault();
        if (hoveredNode === "none") {
          handleBlurAnnotations();
        }

        if (canvasRef.current) {
          const pointer = canvasRef.current.getPointerPosition();
          startMouseDownPoint.current = pointer;
        }

        if (isLeftClick(event) && canPan) {
          // Store the initial mouse position for panning
          if (canvasRef.current) {
            const stage = canvasRef.current;
            const pointer = stage.getPointerPosition();
            if (pointer) {
              downPoint.current = { x: pointer.x, y: pointer.y };
              setIsDragging?.(true);
              setCurrentCursor("grabbing");
            }
          }
        }

        if (!canvasRef.current) return;
        if (
          canDrawMoreBoundingBoxes &&
          event.evt.button === 0 &&
          !event.evt.ctrlKey &&
          enableAnnotation &&
          isMouseInsideImage &&
          isEditing &&
          canDraw
        ) {
          startDrawingBoundingBox(event);
        }
      },
      [
        canPan,
        canDrawMoreBoundingBoxes,
        enableAnnotation,
        hoveredNode,
        isEditing,
        isMouseInsideImage,
        setIsDragging,
        handleBlurAnnotations,
        startDrawingBoundingBox,
        canDraw,
      ]
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging?.(false);
      downPoint.current = null;
      if (enablePanZoom) {
        setCurrentCursor(isEditing && enableAnnotation && canDraw ? "crosshair" : "default");
      }
    }, [enablePanZoom, isEditing, enableAnnotation, setIsDragging, canDraw]);

    // This effect is for handling mouse up event when the mouse is dragging outside the canvas
    useEffect(() => {
      if (!isMouseInsideCanvasOrImage && isDragging) handleMouseUp();
    }, [isMouseInsideCanvasOrImage, isDragging, handleMouseUp]);

    const handleWheel = useCallback(
      (event: KonvaEventObject<WheelEvent>): void => {
        if (canPan || canZoom) {
          event.evt.preventDefault();
          // Ctrl+wheel for zoom
          if (canZoom && event.evt.ctrlKey) {
            scaleOnScroll(event);
          } else if (canPan && event.evt.shiftKey) {
            // Shift+wheel for horizontal pan
            moveCanvas(event, "x");
          } else if (canPan) {
            // Default: vertical panning (mouse wheel or two-finger trackpad gesture)
            moveCanvas(event);
          }
        }
      },
      [canPan, canZoom, scaleOnScroll, moveCanvas]
    );

    const handleMouseMove = useCallback(
      (e: KonvaEventObject<MouseEvent>) => {
        const stage = e.currentTarget.getStage();
        const mousePosition = stage?.getPointerPosition() ?? null;

        mousePositionRef.current = mousePosition;

        if (canPan && isDragging) {
          if (!mouseMoved) setMouseMoved(true);
          if (stage) {
            if (mousePosition && downPoint.current) {
              // For an intuitive and consistent drag, we simply calculate the movement difference
              // without considering the flip state
              const diff = {
                x: mousePosition.x - downPoint.current.x,
                y: mousePosition.y - downPoint.current.y,
              };

              // Calculate new position
              const newX = position.x + diff.x;
              const newY = position.y + diff.y;

              // Apply limits using utility function
              setPosition({
                x: applyPositionLimits(newX, true),
                y: applyPositionLimits(newY, false),
              });

              downPoint.current = mousePosition;
              isSetPositionRef.current = true;
            }
          }
        }

        if (canvasRef.current) {
          const newPointerPosition = getPointerPosition(canvasRef);
          setRelativePointerPosition(newPointerPosition);
          setCurrentCursor(getCursor());
        }
      },
      [position, isDragging, mouseMoved, canPan, setPosition, getCursor, applyPositionLimits]
    );

    useEffect(() => {
      if (!boxContainerRef.current) {
        return () => {};
      }

      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { width, height } = entry.contentRect;
          setBoxContainerSize({ width, height });
        });
      });

      resizeObserver.observe(boxContainerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [setBoxContainerSize]);

    useEffect(() => {
      if (zoom <= 1) {
        setPosition(centerPoint);
      }
    }, [zoom, setPosition, centerPoint]);

    // Resets the cursor when the mouse leaves the canvas
    const handleMouseLeave = () => {
      setIsMouseInsideCanvas(false);
      setRelativePointerPosition(null);
      setHoveredNode("none");
      setIsDragging?.(false);
    };

    const updateBoundingBox = useCallback(
      (annotationUpdate: CanvasAnnotationUpdate, annotation: CanvasAnnotation) => {
        if (setAnnotations) {
          setAnnotations((oldAnnotations) =>
            oldAnnotations.map((previousAnnotation) => {
              if (previousAnnotation.id === annotation.id) {
                const newAnnotation: CanvasAnnotation = {
                  ...annotation,
                  ...annotationUpdate,
                  start: {
                    x: annotationUpdate.start?.x ?? annotation.start.x,
                    y: annotationUpdate.start?.y ?? annotation.start.y,
                  },
                  end: {
                    x: annotationUpdate.end?.x ?? annotation.end.x,
                    y: annotationUpdate.end?.y ?? annotation.end.y,
                  },
                };

                return newAnnotation;
              }
              return previousAnnotation;
            })
          );
        }
      },
      [setAnnotations]
    );

    // Create a factory function for creating update handlers
    const createUpdateHandler = useCallback(
      (annotation: CanvasAnnotation) => (annotationUpdate: Partial<CanvasAnnotation>) =>
        updateBoundingBox(annotationUpdate, annotation),
      [updateBoundingBox]
    );

    // Further optimize by memoizing the annotation processing
    const memorizedAnnotations: CanvasAnnotationUpdater[] = useMemo(
      () =>
        annotations.map((annotation) => {
          const { start, end } = parseGeometryToPoints(annotation, rotatedDimensions);
          // Use the factory function to create the update handler
          const handleUpdateBBox = createUpdateHandler(annotation);
          return {
            ...annotation,
            start,
            end,
            handleUpdateBBox,
          };
        }),
      [annotations, rotatedDimensions, createUpdateHandler]
    );

    // Handle click event on bounding boxes
    const handleClick = useCallback(
      (event: KonvaEventObject<MouseEvent>, annotationId: string) => {
        if (!canInteract) return;
        setDoubleClickedAnnotation(null);
        onAnnotationClick?.(annotationId);
        if (event.evt.button === 0 && !event.evt.ctrlKey) {
          setSelectedAnnotation?.(annotations.filter((annotation) => annotation.id === annotationId)[0]);
          setHoveredNode("active-bounding-box");
          // Force a redraw of the layer to ensure the delete icon appears immediately
          const layer = event.target.getLayer();
          if (layer) {
            layer.batchDraw();
          }
        }
      },
      [canInteract, setSelectedAnnotation, annotations, setHoveredNode, onAnnotationClick]
    );

    const contextMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Handle context menu. In this case shows up the option for downloading the image
    const handleContextMenu = useCallback((e: KonvaEventObject<MouseEvent>) => {
      e.evt.preventDefault(); // Prevent browser context menu

      const stage = e.currentTarget.getStage();
      const currentPosition = stage?.getPointerPosition() ?? null;

      // Clear previous timeout
      if (contextMenuTimeoutRef.current) {
        clearTimeout(contextMenuTimeoutRef.current);
      }

      // The opening of the context menu is delayed N milliseconds to allow the
      // user to pan if they move the mouse before the timeout
      contextMenuTimeoutRef.current = setTimeout(() => {
        if (
          mousePositionRef.current &&
          currentPosition &&
          mousePositionRef.current.x === currentPosition.x &&
          mousePositionRef.current.y === currentPosition.y
        ) {
          setContextMenu({
            visible: true,
            x: e.evt.clientX,
            y: e.evt.clientY,
          });
        }
        contextMenuTimeoutRef.current = null; // Clear after running
      }, SHOW_CONTEXT_MENU_DELAY_MS);
    }, []);

    // Closes context menu
    const closeContextMenu = useCallback(() => {
      setContextMenu({ ...contextMenu, visible: false });
    }, [contextMenu]);

    const canvasRendererProps: Omit<CanvasRendererProps, "isExportMode" | "canvasRef" | "zoom"> = {
      boxContainerSize,
      style,
      currentCursor,
      rotationValue,
      offset,
      position,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseEnter: () => setIsMouseInsideCanvas(true),
      onMouseLeave: handleMouseLeave,
      onMouseDown: handleMouseDown,
      onWheel: handleWheel,
      onContextMenu: handleContextMenu,
      setIsMouseInsideImage,
      canvasImageRef,
      image,
      renderedImageWidth: imageWidth,
      renderedImageHeight: imageHeight,
      canDraw,
      isMouseInsideImage,
      relativePointerPosition,
      rotatedCanvasDimensions,
      showBoundingBoxes,
      memorizedAnnotations,
      setHoveredNode,
      canInteract,
      isDragging,
      selectedAnnotation,
      hideLabel,
      deleteAnnotation,
      onAnnotationClick: handleClick,
      zoomedDimensions,
      normalImageDimensions,
      hideAnnotationMiddleCross,
      hideAnnotationDelete,
      drawingStartPoint,
      drawingEndPoint,
      label,
      annotationStrokeColor,
      annotationStrokeWidth,
      annotationShape,
      imageSettings,
      isEditing,
      rotatedDimensions,
      annotationFontSize: DEFAULT_ANNOTATION_FONT_SIZE,
      doubleClickedAnnotation,
      onDoubleClickAnnotation: (annotation) => setDoubleClickedAnnotation(annotation),
    };

    const generateImageDataUrl = async (): Promise<string | undefined> => {
      const container = document.createElement("div");
      document.body.appendChild(container);

      return new Promise((resolve) => {
        ReactDOM.render(
          <CanvasRenderer
            {...canvasRendererProps}
            isExportMode
            image={originalCanvasImage}
            canvasRef={canvasExportRef}
            zoom={1}
          />,
          container,
          () => {
            const dataURL = canvasExportRef.current?.toDataURL();

            ReactDOM.unmountComponentAtNode(container);
            document.body.removeChild(container);

            resolve(dataURL);
          }
        );
      });
    };

    const downloadImage = async (filename: string) => {
      const imageDataUrl = await generateImageDataUrl();

      if (!imageDataUrl) {
        return;
      }

      // downloading
      const link = document.createElement("a");
      link.download = filename ?? "image-query.png";
      link.href = imageDataUrl;
      link.click();
    };

    const downloadOriginalImage = async (filename: string) => {
      if (!originalCanvasImage) {
        return;
      }

      // Create a temporary canvas to draw the original image
      const canvas = document.createElement("canvas");
      canvas.width = originalCanvasImage.width;
      canvas.height = originalCanvasImage.height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return;
      }

      ctx.drawImage(originalCanvasImage, 0, 0);

      const imageDataUrl = canvas.toDataURL();

      const link = document.createElement("a");
      link.download = filename;
      link.href = imageDataUrl;
      link.click();
    };

    useImperativeHandle(ref, () => ({
      downloadImage: async (filename: string) => downloadImage(filename),
      toDataUrl: generateImageDataUrl,
      blurAnnotations: handleBlurAnnotations,
      generateImage: async () => {
        const imageDataUrl = await generateImageDataUrl();

        if (!imageDataUrl) {
          return null;
        }

        const file = getFileFromDataUrl(imageDataUrl, "image.png", "image/png");
        return file;
      },
    }));

    return (
      <Box
        ref={boxContainerRef}
        id="canvas"
        sx={{
          background: "#C8CDD3",
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          id="canvas-container"
          sx={{
            // Absolute position to leave the parent element to calculate the canvas
            // size on resize
            position: "absolute",
          }}
        >
          <CanvasRendererWrapper
            imageStatus={imageStatus}
            normalImageDimensions={normalImageDimensions}
            imageUrl={imageUrl}
          >
            <CanvasRenderer {...canvasRendererProps} isExportMode={false} canvasRef={canvasRef} zoom={zoom} />
          </CanvasRendererWrapper>
          {/* Context Menu to handle right click without dragging */}
          {contextMenu.visible && (
            <ContextMenu
              position={{ x: contextMenu.x, y: contextMenu.y }}
              onClose={closeContextMenu}
              onDownload={async () => {
                if (canvasRef.current) {
                  await downloadImage(`annotated-image-${iqId}.png`);
                  closeContextMenu();
                }
              }}
              onDownloadOriginal={async () => {
                await downloadOriginalImage(`image-${iqId}.png`);
                closeContextMenu();
              }}
            />
          )}
        </Box>
      </Box>
    );
  })
);
